import { useState } from "react";
import {
	DotsThreeOutline,
	PencilSimple,
	Trash,
	BookmarkSimple,
	Flag
} from "@phosphor-icons/react";
import { toast } from "react-toastify";
import CustomToast from "src/components/CustomToast/CustomToast";
import { Button } from "src/components/ui/button";
import useReportPost from "src/features/SocialFeedContent/hooks/useReportPost/useReportPost";

interface PostActionsMenuProps {
	postId: string;
	isSaved: boolean;
	isOwner: boolean;
	onEdit?: () => void;
	onDelete?: () => void;
	onSave: () => void;
	onUnsave: () => void;
}

const REPORT_REASONS = ["Spam", "Inappropriate content", "Harassment"];

const PostActionsMenu = ({
	postId,
	isSaved,
	isOwner,
	onEdit,
	onDelete,
	onSave,
	onUnsave
}: PostActionsMenuProps) => {
	const [isOpen, setIsOpen] = useState(false);
	const [showReportReasons, setShowReportReasons] = useState(false);
	const reportPost = useReportPost();

	const handleReport = (reason: string) => {
		reportPost.mutate(
			{ postId, reason },
			{
				onSuccess: () => {
					toast(
						<CustomToast
							type="Success"
							message="Post reported. Thank you for your feedback."
						/>
					);
				},
				onError: () => {
					toast(<CustomToast type="Error" message="Failed to report post" />);
				}
			}
		);
		setShowReportReasons(false);
		setIsOpen(false);
	};

	return (
		<div className="relative">
			<Button
				type="button"
				variant="ghost"
				size="icon"
				className="h-8 w-8 rounded-full"
				onClick={(e) => {
					e.stopPropagation();
					setIsOpen(!isOpen);
				}}
				aria-label="Post actions"
			>
				<DotsThreeOutline
					size={16}
					weight="duotone"
					className="text-muted-foreground"
				/>
			</Button>

			{isOpen && (
				<>
					<div
						className="fixed inset-0 z-10"
						onClick={() => {
							setIsOpen(false);
							setShowReportReasons(false);
						}}
					/>
					<div
						role="menu"
						aria-label="Post actions"
						className="absolute right-0 top-full mt-1 z-20 bg-white rounded-lg shadow-lg border border-border py-1 min-w-[160px]"
					>
						<button
							type="button"
							role="menuitem"
							className="flex items-center gap-2 w-full px-3 py-2 text-sm hover:bg-subtle transition-colors"
							onClick={(e) => {
								e.stopPropagation();
								isSaved ? onUnsave() : onSave();
								setIsOpen(false);
							}}
						>
							{isSaved ? (
								<BookmarkSimple size={16} weight="fill" />
							) : (
								<BookmarkSimple size={16} weight="duotone" />
							)}
							{isSaved ? "Unsave" : "Save"}
						</button>

						{isOwner && onEdit && (
							<button
								type="button"
								role="menuitem"
								className="flex items-center gap-2 w-full px-3 py-2 text-sm hover:bg-subtle transition-colors"
								onClick={(e) => {
									e.stopPropagation();
									onEdit();
									setIsOpen(false);
								}}
							>
								<PencilSimple size={16} weight="duotone" />
								Edit
							</button>
						)}

						{isOwner && onDelete && (
							<button
								type="button"
								role="menuitem"
								className="flex items-center gap-2 w-full px-3 py-2 text-sm text-destructive hover:bg-subtle transition-colors"
								onClick={(e) => {
									e.stopPropagation();
									onDelete();
									setIsOpen(false);
								}}
							>
								<Trash size={16} weight="duotone" />
								Delete
							</button>
						)}

						{!isOwner && (
							<div className="relative">
								<button
									type="button"
									className="flex items-center gap-2 w-full px-3 py-2 text-sm hover:bg-subtle transition-colors"
									onClick={(e) => {
										e.stopPropagation();
										setShowReportReasons(!showReportReasons);
									}}
								>
									<Flag size={16} weight="duotone" />
									Report
								</button>

								{showReportReasons && (
									<div className="border-t border-border mt-1 pt-1">
										{REPORT_REASONS.map((reason) => (
											<button
												key={reason}
												type="button"
												className="flex items-center w-full px-3 py-2 text-sm text-muted-foreground hover:bg-subtle transition-colors pl-9"
												onClick={(e) => {
													e.stopPropagation();
													handleReport(reason);
												}}
											>
												{reason}
											</button>
										))}
									</div>
								)}
							</div>
						)}
					</div>
				</>
			)}
		</div>
	);
};

export default PostActionsMenu;
